import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';

import { ApiService } from '@/features/api';
import { DateFilter, FilterType } from '@/legacy/shared/components/SearchFilter/models/Filter';
import {
  DateProperty,
  DropdownProperty,
  PropertyType,
} from '@/legacy/shared/components/SearchFilter/models/Property';
import useSearchFilter from '@/legacy/shared/components/SearchFilter/useSearchFilter';

import { PaymentsLogRenderer } from './PaymentsLogRenderer';
import { PaymentsLogResponse, Row } from './types';

type PaymentsLogProps = {
  emailKey: string;
};

export const PaymentsLog = ({ emailKey }: PaymentsLogProps): React.JSX.Element => {
  const [page, setPage] = useState(0);
  const {
    filters,
    handleClickFilter,
    handleClickProperty,
    handleCloseFilterPopover,
    handleDeleteFilter,
    readyFilters,
  } = useSearchFilter();
  const url: string = useMemo(() => {
    const res = new URL(`${import.meta.env.APP_URLS_API_URL}/v1/contact/${emailKey}/paymentsLog`);
    const dateFilter: DateFilter = readyFilters.find(
      (filter) => filter.type === FilterType.Date
    ) as DateFilter;
    if (dateFilter && dateFilter.from && dateFilter.to) {
      res.searchParams.append('from', dateFilter.from);
      res.searchParams.append('to', dateFilter.to);
    }
    res.searchParams.append('page', page.toString());
    return res.toString();
  }, [emailKey, page, readyFilters]);
  const { data, error, isLoading } = useQuery({
    queryFn: () => ApiService.get<PaymentsLogResponse>(url),
    queryKey: ['paymentsLog', emailKey, url],
  });
  const rows: Array<Row> =
    data?.payments.map((payment) => ({
      ...payment,
      companyName: payment.debitCompanyName,
      creditDate: new Date(payment.creditDate),
      estimatedAvailability: new Date(payment.estimatedCompletionDate),
      status: payment.paymentStatusValue,
    })) ?? [];
  const filterOptions: Array<DateProperty | DropdownProperty> = useMemo(
    () => [
      {
        label: 'Date',
        name: 'date',
        type: PropertyType.Date,
      },
    ],
    []
  );
  return (
    <PaymentsLogRenderer
      error={error}
      filters={filters}
      filterOptions={filterOptions}
      isLoading={isLoading}
      onClickFilter={handleClickFilter}
      onClickProperty={handleClickProperty}
      onCloseFilterPopover={handleCloseFilterPopover}
      onDeleteFilter={handleDeleteFilter}
      onPageChange={(newPage: number) => setPage(newPage)}
      page={page}
      rows={rows}
      totalRecordCount={rows.length}
    />
  );
};
